import { Reveal } from "@/components/site/Reveal";
import { PageHeader } from "@/components/site/PageParts";
import { SITE } from "@/siteData";
import { useMeta } from "@/useMeta";

const P = ({ children }) => (
  <Reveal>
    <p className="text-base leading-[1.8] text-ink-soft">{children}</p>
  </Reveal>
);

const Steps = ({ title, steps, testid }) => (
  <Reveal>
    <div className="rounded-2xl border border-line bg-paper-2 p-6 sm:p-8" data-testid={testid}>
      <h2 className="font-display text-lg font-bold tracking-tight text-ink sm:text-xl">{title}</h2>
      <ol className="mt-5 space-y-4">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-4">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand font-mono text-xs font-semibold text-white">
              {i + 1}
            </span>
            <span className="pt-0.5 text-base leading-relaxed text-ink-soft">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  </Reveal>
);

export default function DataDeletion() {
  useMeta({
    title: "Data Deletion | Simple Background Remover",
    description:
      "How to remove batches, delete saved photographs, and clear local application data for Simple Background Remover. No user profiles or server-side records exist to delete.",
  });

  return (
    <>
      <PageHeader index="/data-deletion" eyebrow="Your data" title="Data Deletion" />
      <div className="container-x max-w-3xl py-16 sm:py-20">
        <div className="space-y-6">
          <P>
            Simple Background Remover does not create user accounts and does not store customer
            photographs or personal information on servers operated by Hildreth Technologies LLC.
          </P>
          <P>
            Because there is no account or server-side customer record, there is no online account to
            delete.
          </P>

          <div className="space-y-6 pt-4">
            <Steps
              title="To remove an active batch:"
              testid="steps-active-batch"
              steps={[
                "Open Simple Background Remover.",
                "Tap Clear or Start New Batch.",
                "Confirm that the current batch should be discarded.",
              ]}
            />
            <Steps
              title="To delete photographs saved to the device:"
              testid="steps-saved-photos"
              steps={[
                "Open the Photos application or gallery.",
                "Locate the completed photograph.",
                "Delete it.",
                "Remove it from Recently Deleted or Trash when immediate permanent deletion is desired.",
              ]}
            />
            <Steps
              title="To clear application data on Android:"
              testid="steps-android"
              steps={[
                "Open Android Settings.",
                "Select Apps.",
                "Select Simple Background Remover.",
                "Select Storage or Storage & Cache.",
                "Select Clear Storage and Clear Cache.",
              ]}
            />
          </div>

          <P>The application may also be uninstalled to remove its private local files.</P>

          <div className="pt-2">
            <Steps
              title="To clear application data on iPhone:"
              testid="steps-iphone"
              steps={[
                "Open Settings.",
                "Select General.",
                "Select iPhone Storage.",
                "Select Simple Background Remover.",
                "Select Delete App.",
              ]}
            />
          </div>

          <P>Photographs previously saved to Photos must be deleted separately.</P>
          <P>Hildreth Technologies LLC cannot remotely delete photographs stored on a customer’s phone.</P>

          <Reveal>
            <div className="mt-2 rounded-2xl border border-line bg-paper p-6">
              <p className="text-sm text-ink-soft">Questions may be sent to:</p>
              <a
                href={`mailto:${SITE.email}`}
                className="link-underline mt-2 inline-block font-mono text-base text-brand"
                data-testid="data-deletion-email"
              >
                {SITE.email}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
